import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Alert } from "../../Components/Alert";
import DetailSkeleton from "../../Components/DetailSkeleton";
import {
  clearRetryStamp,
  requestGetEmeteraiStamping,
  requestRetryStamp,
  requestDeleteEmeteraiFile,
  clearDeleteStamping,
} from "../../Context/emeterai/actions";
import {
  useEmeteraiDispatch,
  useEmeteraiState,
} from "../../Context/emeterai/context";
import {
  cancelAPIRequest,
  classNames,
  getEmeteraiFileName,
  getErrorMessages,
  getLabelsByCode,
  restampEligible,
} from "../../Utils";
import {
  downloadFileWithFallback,
} from "../../Utils/DownloadUtils";
import { CSButton } from "../../Components/CSButton";
import dayjs from "dayjs";
import Badge from "../../Components/Badge";
import { Modal } from "../../Components/Modal";
import { DocumentIcon } from "@heroicons/react/outline";
import { ModalConfirm } from "../../Components/ModalConfirm";
import { ModalAlert } from "../../Components/ModalAlert";
import { CheckIcon, XIcon } from "@heroicons/react/solid";

const initialSteps = [
  {
    name: "Upload Document",
    description: "The Document Has Been Uploaded",
    status: "complete",
  },
  {
    name: "Serial Number",
    description: "The Serial Number Has Been Issued",
    status: "complete",
  },
  {
    name: "Stamp",
    description: "Stamp Successful",
    status: "complete",
  },
];

export const EmeteraiDetail = () => {
  const { id } = useParams();

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const emeteraiDispatch = useEmeteraiDispatch();
  const {
    request,
    emeteraiStamping,
    loading,
    error,
    retryLoading,
    retryError,
    retryStamp,
    deleteLoading,
    deleteError,
    deletedFile,
  } = useEmeteraiState();

  // Check if file is deleted
  const isFileDeleted = emeteraiStamping?.file?.key === 'Dihapus' || 
                       emeteraiStamping?.file?.key === 'Deleted' || 
                       emeteraiStamping?.file?.key === 'deleted';

  const [downloading, setdownloading] = useState(false);
  const [downloadProgress, setdownloadProgress] = useState(0);
  const downloadRef = useRef(null);
  const [downloadDocError, setdownloadDocError] = useState(null);
  const [showDownloadErrorModal, setshowDownloadErrorModal] = useState(false);

  const [selectedRetry, setselectedRetry] = useState({});
  const [showRetryModal, setshowRetryModal] = useState(false);
  const [showSuccessRetryModal, setshowSuccessRetryModal] = useState(false);
  const [showErrorRetryModal, setshowErrorRetryModal] = useState(false);
  const [showDeleteModal, setshowDeleteModal] = useState(false);
  const [showSuccessDeleteModal, setshowSuccessDeleteModal] = useState(false);
  const [showErrorDeleteModal, setshowErrorDeleteModal] = useState(false);
  const [steps, setsteps] = useState([]);

  useEffect(() => {
    requestGetEmeteraiStamping(emeteraiDispatch, { id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (selectedRetry.id) {
      setshowRetryModal(true);
    } else {
      setshowRetryModal(false);
    }
  }, [selectedRetry.id]);

  useEffect(() => {
    if (retryStamp.id) {
      setselectedRetry({});
      setshowSuccessRetryModal(true);
    }
  }, [retryStamp.id]);

  useEffect(() => {
    if (!retryError) {
    } else {
      setselectedRetry({});
      setshowErrorRetryModal(true);
    }
  }, [retryError]);

  useEffect(() => {
    if (!downloadDocError) {
      setshowDownloadErrorModal(false);
    } else {
      setshowDownloadErrorModal(true);
    }
  }, [downloadDocError]);

  useEffect(() => {
    if (deletedFile.id) {
      setshowSuccessDeleteModal(true);
    }
  }, [deletedFile.id]);

  useEffect(() => {
    if (!deleteError) {
    } else {
      setshowErrorDeleteModal(true);
    }
  }, [deleteError]);

  useEffect(() => {
    if (emeteraiStamping?.status) {
      let updatedSteps = initialSteps;
      if (emeteraiStamping.status === "PENDING") {
        updatedSteps[0]["status"] = "complete";
        updatedSteps[1]["status"] = "complete";
        updatedSteps[2]["status"] = "current";
        updatedSteps[2]["description"] = "Processing Stamp";
      } else if (emeteraiStamping.status === "SUCCESS") {
        updatedSteps[0]["status"] = "complete";
        updatedSteps[1]["status"] = "complete";
        updatedSteps[2]["status"] = "complete";
      } else if (emeteraiStamping.status === "FAILED") {
        updatedSteps[0]["status"] = "complete";
        updatedSteps[1]["status"] = "complete";
        updatedSteps[2]["status"] = "failed";
        updatedSteps[2]["description"] = "Stamp Failed";
        if (updatedSteps.length === 3) {
          updatedSteps.push({
            name: "Quota",
            description: "Quota Has Been Restored",
            status: "complete",
          });
        }
      }
      setsteps(updatedSteps);
    }
  }, [emeteraiStamping?.status]);

  const onDownloadProgress = (progress) => {
    const { transferredBytes, totalBytes } = progress;
    if (!totalBytes) {
      setdownloadProgress(0);
    } else {
      setdownloadProgress({ loaded: transferredBytes, total: totalBytes });
    }
  };

  
  const downloadFile = async (key) => {
    try {
      setdownloading(true);
      
      await downloadFileWithFallback(
        key,
        onDownloadProgress,
        getEmeteraiFileName,
        downloadRef
      );
    } catch (error) {
      setdownloadDocError(error);
    } finally {
      setdownloading(false);
      setdownloadProgress(0);
    }
  };

  const renderProgressBar = (downloadProgress) => {
    const total = downloadProgress?.total || 1;
    const percentage = Math.round(
      ((downloadProgress?.loaded || 0) / total) * 100
    );

    return (
      <div
        className="bg-blue-600 text-xs font-medium text-blue-100 text-center p-0.5 leading-none rounded-full"
        style={{ width: `${percentage}%` }}
      >
        {`${percentage}%`}
      </div>
    );
  };

  const renderFileModal = () => (
    <Modal
      title="Download File"
      show={downloading}
      onClose={() => {
        if (downloadRef.current) {
          const downloadTask = downloadRef.current;
          downloadTask.cancel();
        }
        setdownloading(false);
      }}
      footer={
        <div className="p-4 flex flex-row-reverse gap-2 justify-between items-center border-t border-gray-300 bg-gray-50 rounded-b-lg">
          <div className="flex-1 overflow-hidden">
            <div className="w-full">
              <p className="text-xs text-slate-500">{t("Downloading")}</p>
              <div className="w-full mt-1 bg-gray-200 rounded-md sm:rounded-lg dark:bg-gray-700">
                {renderProgressBar(downloadProgress)}
              </div>
            </div>
          </div>
        </div>
      }
    >
      <div className="bg-white p-4">
        <div className="flex flex-col justify-center items-center">
          <DocumentIcon className="h-24 w-24 text-slate-500" />
          <p className="text-xs font-medium">
            {Math.round(downloadProgress?.total / 1024 || 0)} KB
          </p>
        </div>
      </div>
    </Modal>
  );

  const renderDownloadError = () => {
    if (downloadDocError) {
      return (
        <ModalConfirm
          open={showDownloadErrorModal}
          type="ALERT"
          setOpen={(open) => {
            setshowDownloadErrorModal(open);
          }}
          title="Error"
          description={getErrorMessages(downloadDocError)}
          onConfirm={() => {
            setdownloadDocError(null);
          }}
        />
      );
    }
    return null;
  };

  const renderRetryDialogModal = () => {
    if (selectedRetry.id) {
      return (
        <ModalAlert
          open={showRetryModal}
          setOpen={(open) => {
            setshowRetryModal(open);
          }}
          onCancel={() => {
            cancelAPIRequest(request);
            setselectedRetry({});
          }}
          title="Retry E-Meterai Stamping"
          description="Are you sure to retry stamping?"
          type="warning"
          confirmButtonText="Yes"
          cancelButtonText="Cancel"
          loadingButton={retryLoading}
          disabledButton={retryLoading}
          onConfirm={() => {
            requestRetryStamp(emeteraiDispatch, {
              id: selectedRetry.id,
            });
          }}
        />
      );
    }
    return null;
  };

  const renderRetrySuccess = () => {
    if (retryStamp.id) {
      return (
        <ModalConfirm
          open={showSuccessRetryModal}
          type="SUCCESS"
          setOpen={(open) => {
            setshowSuccessRetryModal(open);
          }}
          title="Success"
          description="The stamping request is being processed, check the stamping result on the Stamping History page"
          onConfirm={() => {
            window.location.href = `/emeterai/detail/${retryStamp.id}`;
            clearRetryStamp(emeteraiDispatch);
          }}
        />
      );
    }
    return null;
  };

  const renderRetryError = () => {
    if (retryError) {
      return (
        <ModalConfirm
          open={showErrorRetryModal}
          type="ALERT"
          setOpen={(open) => {
            setshowErrorRetryModal(open);
          }}
          title="Error"
          description={getErrorMessages(retryError)}
          onConfirm={() => {
            clearRetryStamp(emeteraiDispatch);
          }}
        />
      );
    }
    return null;
  };

  const renderDeleteModal = () => {
    return (
      <ModalAlert
        open={showDeleteModal}
        setOpen={(open) => {
          setshowDeleteModal(open);
        }}
        onCancel={() => {
          setshowDeleteModal(false);
        }}
        title={t("deleteDocument")}
        description={t("deleteDocumentConfirmation")}
        type="warning"
        confirmButtonText={t("Yes")}
        cancelButtonText={t("Cancel")}
        loadingButton={deleteLoading}
        disabledButton={deleteLoading}
        onConfirm={() => {
          setshowDeleteModal(false);
          requestDeleteEmeteraiFile(emeteraiDispatch, {
            id: emeteraiStamping.id,
          });
        }}
      />
    );
  };

  const renderDeleteSuccess = () => {
    if (deletedFile.id) {
      return (
        <ModalConfirm
          open={showSuccessDeleteModal}
          type="SUCCESS"
          setOpen={(open) => {
            setshowSuccessDeleteModal(open);
          }}
          title={t("Success")}
          description={t("documentSuccessDeleted")}
          onConfirm={() => {
            requestGetEmeteraiStamping(emeteraiDispatch, { id });
            clearDeleteStamping(emeteraiDispatch);
          }}
        />
      );
    }
    return null;
  };

  const renderDeleteError = () => {
    if (deleteError) {
      return (
        <ModalConfirm
          open={showErrorDeleteModal}
          type="ALERT"
          setOpen={(open) => {
            setshowErrorDeleteModal(open);
          }}
          title={t("Error")}
          description={getErrorMessages(deleteError)}
          onConfirm={() => {
            clearDeleteStamping(emeteraiDispatch);
          }}
        />
      );
    }
    return null;
  };

  const renderStep = (step, stepIdx) => {
    if (step.status === "current") {
      return (
        <div>
          {stepIdx !== steps.length - 1 ? (
            <div
              className="-ml-px absolute mt-0.5 top-4 left-4 w-0.5 h-full bg-gray-300"
              aria-hidden="true"
            />
          ) : null}
          <div className="relative flex items-start group" aria-current="step">
            <span className="h-9 flex items-center" aria-hidden="true">
              <span className="relative z-10 w-8 h-8 flex items-center justify-center bg-white border-2 border-blue-600 rounded-full">
                <span className="h-2.5 w-2.5 bg-blue-600 rounded-full" />
              </span>
            </span>
            <span className="ml-4 min-w-0 flex flex-col">
              <span className="text-xs font-semibold tracking-wide uppercase text-blue-600">
                {t(step.name)}
              </span>
              <span className="text-sm text-gray-500">
                {t(step.description)}
              </span>
            </span>
          </div>
        </div>
      );
    }
    if (step.status === "complete") {
      return (
        <div>
          {stepIdx !== steps.length - 1 ? (
            <div
              className="-ml-px absolute mt-0.5 top-4 left-4 w-0.5 h-full bg-blue-600"
              aria-hidden="true"
            />
          ) : null}
          <div className="relative flex items-start group">
            <span className="h-9 flex items-center">
              <span className="relative z-10 w-8 h-8 flex items-center justify-center bg-blue-600 rounded-full">
                <CheckIcon className="w-5 h-5 text-white" aria-hidden="true" />
              </span>
            </span>
            <span className="ml-4 min-w-0 flex flex-col">
              <span className="text-xs font-semibold tracking-wide uppercase">
                {t(step.name)}
              </span>
              <span className="text-sm text-gray-500">
                {t(step.description)}
              </span>
            </span>
          </div>
        </div>
      );
    }
    if (step.status === "failed") {
      return (
        <div>
          {stepIdx !== steps.length - 1 ? (
            <div
              className="-ml-px absolute mt-0.5 top-4 left-4 w-0.5 h-full bg-red-600"
              aria-hidden="true"
            />
          ) : null}
          <div className="relative flex items-start group">
            <span className="h-9 flex items-center">
              <span className="relative z-10 w-8 h-8 flex items-center justify-center bg-red-600 rounded-full">
                <XIcon className="w-5 h-5 text-white" aria-hidden="true" />
              </span>
            </span>
            <span className="ml-4 min-w-0 flex flex-col">
              <span className="text-xs font-semibold tracking-wide uppercase">
                {t(step.name)}
              </span>
              <span className="text-sm text-gray-500">
                {t(step.description)}
              </span>
            </span>
          </div>
        </div>
      );
    }
    return (
      <div>
        {stepIdx !== steps.length - 1 ? (
          <div
            className="-ml-px absolute mt-0.5 top-4 left-4 w-0.5 h-full bg-gray-300"
            aria-hidden="true"
          />
        ) : null}
        <div className="relative flex items-start group">
          <span className="h-9 flex items-center" aria-hidden="true">
            <span className="relative z-10 w-8 h-8 flex items-center justify-center bg-white border-2 border-gray-300 rounded-full"></span>
          </span>
          <span className="ml-4 min-w-0 flex flex-col">
            <span className="text-xs font-semibold tracking-wide uppercase text-gray-500">
              {t(step.name)}
            </span>
            <span className="text-sm text-gray-500">{t(step.description)}</span>
          </span>
        </div>
      </div>
    );
  };

  if (!emeteraiStamping || error) {
    return (
      <div className="flex flex-col gap-6">
        <div className="bg-white min-h-full rounded-lg p-4">
          <Alert
            type="error"
            title="Error"
            message={error?.message || "E-Meterai not found."}
            errors={error?.errors}
          />
        </div>
        <div className="bg-white p-4 rounded-lg">
          <CSButton />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="max-w-3xl grid grid-cols-1 gap-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
        <div className="lg:col-start-3 lg:col-span-1">
          {loading ? (
            <div className="bg-white animate-pulse rounded-lg p-4 flex items-center">
              <div className="h-8 w-8 mr-2 bg-gray-300 rounded-full"></div>
              <div>
                <div className="h-4 w-24 my-1 bg-gray-300 rounded-md"></div>
                <div className="h-4 w-48 my-1 bg-gray-300 rounded-md"></div>
              </div>
            </div>
          ) : (
            <div>
              {steps.length > 0 && (
                <div className="bg-white rounded-lg p-4">
                  <ol className="overflow-hidden">
                    {steps.map((step, stepIdx) => (
                      <li
                        key={step.name}
                        className={classNames(
                          stepIdx !== steps.length - 1 ? "pb-10" : "",
                          "relative"
                        )}
                      >
                        {renderStep(step, stepIdx)}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="bg-white rounded-lg lg:col-start-1 lg:col-span-2">
          <div className="px-4 py-5 sm:px-6">
            <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">
                  {t("Status")}
                </dt>
                {loading ? (
                  <DetailSkeleton />
                ) : (
                  <dd className="mt-1 text-sm text-gray-900">
                    {emeteraiStamping.status === "PENDING" ? (
                      <Badge title="Pending" type="alert" />
                    ) : null}
                    {emeteraiStamping.status === "SUCCESS" ? (
                      <Badge title="Success" type="success" />
                    ) : null}
                    {emeteraiStamping.status === "FAILED" ? (
                      <Badge title="Failed" type="danger" />
                    ) : null}
                    {emeteraiStamping.status === "EXPIRED" ? (
                      <Badge title="Expired" type="secondary" />
                    ) : null}
                  </dd>
                )}
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">
                  {t("File Name")}
                </dt>
                {loading ? (
                  <DetailSkeleton />
                ) : (
                  <dd className="mt-1 text-sm text-gray-900">
                    {getEmeteraiFileName(
                      emeteraiStamping.file?.key,
                      emeteraiStamping.id
                    )}
                  </dd>
                )}
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">
                  {t("File Type")}
                </dt>
                {loading ? (
                  <DetailSkeleton />
                ) : (
                  <dd className="mt-1 text-sm text-gray-900">
                    {getLabelsByCode(emeteraiStamping.documentType)}
                  </dd>
                )}
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">
                  {t("Doc Date")}
                </dt>
                {loading ? (
                  <DetailSkeleton />
                ) : (
                  <dd className="mt-1 text-sm text-gray-900">
                    {emeteraiStamping.documentDate
                      ? dayjs(emeteraiStamping.documentDate)
                          .locale(i18n.language)
                          .format("DD MMM YYYY")
                      : "-"}
                  </dd>
                )}
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">
                  {t("Doc Number")}
                </dt>
                {loading ? (
                  <DetailSkeleton />
                ) : (
                  <dd className="mt-1 text-sm text-gray-900">
                    {emeteraiStamping.documentNumber || "-"}
                  </dd>
                )}
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">
                  {t("Serial Number")}
                </dt>
                {loading ? (
                  <DetailSkeleton />
                ) : (
                  <dd className="mt-1 text-sm text-gray-900">
                    {emeteraiStamping.serialNumber || "-"}
                  </dd>
                )}
              </div>
            </dl>
          </div>
          {(emeteraiStamping.status === "PENDING" ||
            emeteraiStamping.status === "SUCCESS") && (
            <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("Dokumen")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {emeteraiStamping.status === "SUCCESS" &&
                    emeteraiStamping.file?.key && !isFileDeleted ? (
                      <button
                        className="text-left"
                        onClick={() => {
                          downloadFile(emeteraiStamping.file.key);
                        }}
                      >
                        <span className="text-sm text-blue-600 underline">
                          {t("Download")}
                        </span>
                      </button>
                    ) : isFileDeleted ? (
                      <span>{t("File Deleted")}</span>
                    ) : (
                      <span>{t("On Process")}</span>
                    )}
                  </dd>
                </div>
              </dl>
            </div>
          )}
          {emeteraiStamping.status === "SUCCESS" && (
            <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("Action")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {emeteraiStamping.file?.key && !isFileDeleted ? (
                      <div className="flex flex-col space-y-2 items-start">
                        <button onClick={() => navigate(`/emeterai?id=${id}`)} disabled={deleteLoading} className={deleteLoading ? "cursor-not-allowed" : "cursor-pointer"}>
                           <span className="text-sm text-blue-600 underline">
                             {t("addMoreEmeterai")}
                           </span>
                         </button>
                        <button onClick={() => setshowDeleteModal(true)} disabled={deleteLoading} className={deleteLoading ? "cursor-not-allowed" : "cursor-pointer"}>
                          <span className={`text-sm underline ${deleteLoading ? 'text-gray-400' : 'text-red-600'}`}>
                            {deleteLoading ? t("Deleting...") : t("deleteDocument")}
                          </span>
                        </button>
                      </div>
                    ) : isFileDeleted ? (
                      <span>{t("No Actions Available")}</span>
                    ) : (
                      <span>{t("On Process")}</span>
                    )}
                  </dd>
                </div>
              </dl>
            </div>
          )}
          {emeteraiStamping.status === "FAILED" && emeteraiStamping.error && (
            <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
              {restampEligible(emeteraiStamping.error) ? (
                <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
                  <div className="sm:col-span-1">
                    <dt className="text-sm font-medium text-gray-500">
                      {t("Error")}
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {emeteraiStamping.error}
                    </dd>
                  </div>
                  <div className="sm:col-span-1">
                    <dt className="text-sm font-medium text-gray-500">
                      {t("Action")}
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {emeteraiStamping.file?.key && !isFileDeleted ? (
                        <div className="flex flex-col space-y-2 items-start">
                          <button
                            onClick={() => {
                              setselectedRetry(emeteraiStamping);
                            }}
                            disabled={deleteLoading}
                            className={deleteLoading ? "cursor-not-allowed" : "cursor-pointer"}
                           >
                             <span className="text-sm text-blue-600 underline">
                               {t("Restamp")}
                             </span>
                           </button>
                          <button onClick={() => setshowDeleteModal(true)} disabled={deleteLoading} className={deleteLoading ? "cursor-not-allowed" : "cursor-pointer"}>
                            <span className={`text-sm underline ${deleteLoading ? 'text-gray-400' : 'text-red-600'}`}>
                              {deleteLoading ? t("Deleting...") : t("deleteDocument")}
                            </span>
                          </button>
                        </div>
                      ) : isFileDeleted ? (
                        <span>{t("No Actions Available")}</span>
                      ) : (
                        <button
                          onClick={() => {
                            setselectedRetry(emeteraiStamping);
                          }}
                          disabled={deleteLoading}
                          className={deleteLoading ? "cursor-not-allowed" : "cursor-pointer"}
                         >
                           <span className="text-sm text-blue-600 underline">
                             {t("restamp")}
                           </span>
                         </button>
                      )}
                    </dd>
                  </div>
                </dl>
              ) : (
                <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
                  <div className="sm:col-span-1">
                    <dt className="text-sm font-medium text-gray-500">
                      {t("Error")}
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      <Alert
                        type="error"
                        title="Error"
                        message={emeteraiStamping.error}
                      />
                    </dd>
                  </div>
                  <div className="sm:col-span-1">
                    <dt className="text-sm font-medium text-gray-500">
                      {t("Action")}
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {emeteraiStamping.file?.key && !isFileDeleted ? (
                        <button onClick={() => setshowDeleteModal(true)} disabled={deleteLoading} className={deleteLoading ? "cursor-not-allowed" : "cursor-pointer"}>
                          <span className={`text-sm underline ${deleteLoading ? 'text-gray-400' : 'text-red-600'}`}>
                            {deleteLoading ? t("Deleting...") : t("deleteDocument")}
                          </span>
                        </button>
                      ) : isFileDeleted ? (
                        <span>{t("No Actions Available")}</span>
                      ) : (
                        <span>{t("No Actions Available")}</span>
                      )}
                    </dd>
                  </div>
                </dl>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="max-w-3xl grid grid-cols-1 gap-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
        <div className="col-span-2 bg-white p-4 rounded-lg">
          <CSButton />
        </div>
      </div>
      {renderFileModal()}
      {renderDownloadError()}
      {renderRetryDialogModal()}
      {renderRetrySuccess()}
      {renderRetryError()}
      {renderDeleteModal()}
      {renderDeleteSuccess()}
      {renderDeleteError()}
    </div>
  );
};
